// src/evaluations/potentialReport/PotentialReportPages.js
// Hojas del Informe de Potencial (PLAN_UNIFICACION_ASSESSMENT.md §7).
// Misma estructura y clases que la plantilla del PDF en el backend
// (server/src/utils/assessment/potentialReportTemplate.js): si se cambia una,
// cambiar la otra. Recibe el informe ya resuelto por el backend (niveles,
// colores, textos, gráficos y hojas); aquí solo se dibuja.
import React, { useState } from 'react';
import { ReactComponent as DiscBaseSvg } from '../../disc/DISC.svg';

const AXIS_TICKS = [0, 20, 40, 60, 80, 100];

const cls = (...names) => names.filter(Boolean).join(' ');

// ---------------------------------------------------------------- piezas

// Silueta de la portada (Amanda no guarda fotos del candidato)
function Silhouette() {
    return (
        <svg viewBox="0 0 100 100" aria-hidden="true">
            <circle cx="50" cy="36" r="21" fill="#274579" />
            <path d="M8 100 C8 74 27 62 50 62 C73 62 92 74 92 100 Z" fill="#274579" />
        </svg>
    );
}

function Logo({ url }) {
    const [failed, setFailed] = useState(false);
    if (!url || failed) return null;
    return <img src={url} alt="" onError={() => setFailed(true)} />;
}

function NaBox({ section, texts }) {
    return (
        <div className="pr-na-box">
            <div className="pr-na-title">{texts.notAvailable}</div>
            {section && section.reasonText ? <div className="pr-na-reason">{section.reasonText}</div> : null}
        </div>
    );
}

function List({ items, texts, clamp = 2, extraClass = '' }) {
    if (!items || items.length === 0) return <div className="pr-na">{texts.noItems}</div>;
    return (
        <ul className={cls('pr-list', extraClass)}>
            {items.map((item, index) => (
                <li key={index} className={`pr-clamp-${clamp}`}>{item}</li>
            ))}
        </ul>
    );
}

function Box({ area, title, children }) {
    return (
        <div className={`pr-box pr-area-${area}`}>
            <div className="pr-box-title">{title}</div>
            <div className="pr-box-body">{children}</div>
        </div>
    );
}

function SubBox({ area, title, children }) {
    return (
        <div className={`pr-box pr-area-${area}`}>
            <div className="pr-sub-title">{title}</div>
            <div className="pr-box-body">{children}</div>
        </div>
    );
}

function PlainBox({ area, children }) {
    return (
        <div className={`pr-box pr-area-${area}`}>
            <div className="pr-box-body">{children}</div>
        </div>
    );
}

function Field({ label, value, texts, reasonText }) {
    const has = value !== null && value !== undefined && value !== '';
    const shown = has ? value : [texts.notAvailable, reasonText].filter(Boolean).join(' · ');
    return (
        <div className="pr-field">
            <div className="pr-field-label">{label}</div>
            <div className={has ? 'pr-field-value' : 'pr-field-value pr-na'}>{shown}</div>
        </div>
    );
}

function Bars({ rows, dense = false, wide = false, note = '' }) {
    return (
        <>
            <div className={cls('pr-bars', dense && 'pr-bars-dense', wide && 'pr-bars-wide')}>
                <div className="pr-bars-rows">
                    <div className="pr-bar-grid" />
                    {rows.map((row, index) => (
                        <div className="pr-bar-row" key={`${row.name}-${index}`}>
                            <div className={cls('pr-bar-label', dense ? 'pr-clamp-1' : 'pr-clamp-2')}>{row.name}</div>
                            <div className="pr-bar-track">
                                <div className="pr-bar-fill" style={{ width: `${row.width}%`, background: row.color }}>
                                    {row.valueInside ? (
                                        <span className="pr-bar-value" style={{ color: row.textColor }}>{row.scoreText}</span>
                                    ) : null}
                                </div>
                                {row.valueInside ? null : (
                                    <span className="pr-bar-value pr-bar-value-out">{row.scoreText}</span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                <div className="pr-bar-axis">
                    {AXIS_TICKS.map((tick) => (
                        <span key={tick} style={{ left: `${tick}%` }}>{`${tick}%`}</span>
                    ))}
                </div>
            </div>
            {note ? <div className="pr-note">{note}</div> : null}
        </>
    );
}

function ScaleItems({ scale }) {
    return scale.map((level) => (
        <div className="pr-scale-item" key={level.key}>
            <span className="pr-scale-dot" style={{ background: level.color }} />
            {`${level.label} (${level.rangeLabel})`}
        </div>
    ));
}

function RankingItem({ item }) {
    return (
        <div className="pr-ranking-item">
            <div className="pr-ranking-name">
                <span className="pr-ranking-dot" style={{ background: item.color }} />
                <span>{item.name}</span>
            </div>
            <div className="pr-ranking-score" style={{ color: item.color }}>{item.scoreText}</div>
        </div>
    );
}

function Ranking({ ranked, texts }) {
    if (ranked.mode === 'topBottom') {
        return (
            <div className="pr-ranking">
                <div className="pr-ranking-col">
                    <div className="pr-ranking-head">{texts.mostDeveloped}</div>
                    {ranked.top.map((item) => <RankingItem key={item.id} item={item} />)}
                </div>
                <div className="pr-ranking-col">
                    <div className="pr-ranking-head">{texts.leastDeveloped}</div>
                    {ranked.bottom.map((item) => <RankingItem key={item.id} item={item} />)}
                </div>
            </div>
        );
    }
    if (!ranked.all || ranked.all.length === 0) {
        return <NaBox section={{ reasonText: texts.reasons.noData }} texts={texts} />;
    }
    return (
        <div className="pr-ranking">
            <div className="pr-ranking-col">
                <div className="pr-ranking-head">{texts.rankedCompetencies}</div>
                {ranked.all.map((item) => <RankingItem key={item.id} item={item} />)}
            </div>
        </div>
    );
}

function Gauge({ test }) {
    const g = test.gauge;
    return (
        <div className="pr-gauge">
            <svg width={g.width} height={g.height} viewBox={`0 0 ${g.width} ${g.height}`}>
                <path d={g.track} fill="none" stroke="#e3e3e3" strokeWidth={g.stroke} />
                {g.value ? <path d={g.value} fill="none" stroke={test.color} strokeWidth={g.stroke} /> : null}
            </svg>
            <span className="pr-gauge-text" style={{ color: test.color }}>{test.scoreText}</span>
        </div>
    );
}

function Pie({ chart, title }) {
    const s = chart.size;
    return (
        <div className="pr-pie">
            <div className="pr-pie-title">{title}</div>
            <svg viewBox={`0 0 ${s} ${s}`}>
                {chart.empty ? <circle cx={s / 2} cy={s / 2} r={s / 2 - 1} fill="#eeeeee" /> : null}
                {chart.slices.map((slice) => (slice.full ? (
                    <circle key={slice.letter} cx={slice.cx} cy={slice.cy} r={slice.r} fill={slice.color} />
                ) : (
                    <path key={slice.letter} d={slice.path} fill={slice.color} stroke="#ffffff" strokeWidth="1" />
                )))}
                {chart.slices.filter((slice) => slice.showLabel).map((slice) => (
                    <text
                        key={`label-${slice.letter}`}
                        x={slice.labelX}
                        y={slice.labelY}
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize="11"
                        fontWeight="700"
                        fill={slice.textColor}
                    >
                        {slice.valueText}
                    </text>
                ))}
            </svg>
        </div>
    );
}

// Diamante: el DISC.svg base + la capa con perfil natural y flecha al adaptado
function Diamond({ diamond }) {
    const v = diamond.viewBox;
    const n = diamond.natural;
    const a = diamond.adapted;
    return (
        <div className="pr-diamond">
            <DiscBaseSvg />
            <svg viewBox={`0 0 ${v} ${v}`}>
                <defs>
                    <marker id="prDiscArrow" markerWidth="12" markerHeight="12" refX="7" refY="3.5" orient="auto">
                        <path d="M0,0 L0,7 L7,3.5 z" fill="#111827" />
                    </marker>
                </defs>
                <circle cx={n.x} cy={n.y} r="14" fill="#3b82f6" />
                <line x1={n.x} y1={n.y} x2={a.x} y2={a.y} stroke="#111827" strokeWidth="5" markerEnd="url(#prDiscArrow)" />
                <circle cx={a.x} cy={a.y} r="10" fill="#111827" />
            </svg>
        </div>
    );
}

function Para({ label, text, clamp, texts }) {
    return (
        <p className={`pr-para pr-clamp-${clamp}`}>
            <b>{`${label}:`}</b>{' '}
            {text || <span className="pr-na">{texts.notAvailable}</span>}
        </p>
    );
}

function CompetencyCard({ item, area, texts }) {
    if (!item) return null;
    return (
        <div className={`pr-card pr-area-${area}`} style={{ borderLeftColor: item.color }}>
            <div className="pr-card-head">
                <span className="pr-card-name pr-clamp-1" style={{ color: item.color }}>{item.name}</span>
                <span className="pr-card-score" style={{ color: item.color }}>{item.scoreText}</span>
            </div>
            <div className="pr-card-meta">{`${item.levelLabel} · ${item.answeredLabel}`}</div>
            <div className="pr-card-cols">
                <div className="pr-card-col">
                    <div className="pr-card-col-title">{texts.strengthsForPosition}</div>
                    <List items={item.strengths} texts={texts} clamp={6} extraClass="pr-list-sm" />
                </div>
                <div className="pr-card-col">
                    <div className="pr-card-col-title pr-improve">{texts.improvementAreas}</div>
                    <List items={item.improvements} texts={texts} clamp={6} extraClass="pr-list-sm pr-list-improve" />
                </div>
            </div>
        </div>
    );
}

// ---------------------------------------------------------------- hojas

function PageShell({ report, page, children }) {
    const { texts, cover } = report;
    const name = [cover.applicantName, cover.positionName].filter(Boolean).join(' · ');
    return (
        <section className={`pr-page pr-page-${page.type}`}>
            <header className="pr-header">
                <div className="pr-header-bar">
                    <span className="pr-header-title">{texts.title}</span>
                    <span className="pr-header-name">{name}</span>
                </div>
                <div className="pr-header-logo"><Logo url={cover.logoUrl} /></div>
            </header>
            <div className="pr-body">{children}</div>
            <footer className="pr-footer">
                <span>{cover.footerText}</span>
                <span>{`${page.number} / ${page.total}`}</span>
            </footer>
        </section>
    );
}

function CoverPage({ report }) {
    const { texts, cover } = report;
    return (
        <section className="pr-page pr-cover">
            <div className="pr-cover-top">
                <div className="pr-cover-logo"><Logo url={cover.logoUrl} /></div>
                <div className="pr-cover-id">
                    <h1 className="pr-cover-title">{texts.title}</h1>
                    <div className="pr-cover-photo"><Silhouette /></div>
                    <div className="pr-cover-field"><b>{`${texts.evaluee}:`}</b>{' '}{cover.applicantName || texts.notAvailable}</div>
                    <div className="pr-cover-field"><b>{`${texts.position}:`}</b>{' '}{cover.positionName || texts.notAvailable}</div>
                </div>
            </div>
            <div className="pr-cover-band" />
            <div className="pr-cover-bottom">
                <div className="pr-cover-meta">
                    <div><b>{`${texts.date}:`}</b>{' '}{cover.completedAt || texts.notAvailable}</div>
                    {cover.place ? <div><b>{`${texts.place}:`}</b>{' '}{cover.place}</div> : null}
                </div>
                <div className="pr-cover-mode"><b>{`${texts.modality}:`}</b>{' '}{cover.modeLabel}</div>
            </div>
        </section>
    );
}

function PersonalBox({ report, simple }) {
    const { texts, cover, summary, candidate } = report;
    return (
        <Box area="personal" title={texts.personalData}>
            <Field label={texts.name} value={cover.applicantName || null} texts={texts} />
            {simple ? (
                <>
                    <Field label={texts.position} value={cover.positionName || null} texts={texts} />
                    <Field label={texts.date} value={cover.completedAt} texts={texts} />
                </>
            ) : (
                <Field
                    label={texts.education}
                    value={candidate.education.available ? candidate.education.value : null}
                    texts={texts}
                    reasonText={candidate.education.reasonText}
                />
            )}
            <Field label={texts.confidenceIndex} value={summary.confidenceIndexText} texts={texts} />
        </Box>
    );
}

function CompetencyBarsBox({ report, page, area, wide }) {
    const { texts, competencies } = report;
    if (competencies.bars.length === 0) {
        return <Box area={area} title={texts.competencies}><NaBox section={{ reasonText: texts.reasons.noData }} texts={texts} /></Box>;
    }
    const note = competencies.hiddenBars > 0 ? `+${competencies.hiddenBars} ${texts.moreInDetail}` : '';
    return (
        <Box area={area} title={texts.competencies}>
            <Bars rows={competencies.bars} dense={Boolean(page.denseBars)} wide={wide} note={note} />
        </Box>
    );
}

function NineBoxBox({ report }) {
    const { texts, nineBox } = report;
    if (!nineBox.available) return <Box area="ninebox" title={texts.diagnosis}><NaBox section={nineBox} texts={texts} /></Box>;
    return (
        <Box area="ninebox" title={texts.diagnosis}>
            <div className="pr-ninebox">
                <div className="pr-ninebox-y"><span>{`${texts.jobFitScore} →`}</span></div>
                <div className="pr-ninebox-grid">
                    {nineBox.rows.flat().map((cell) => (
                        <div key={cell.key} className={cell.active ? 'pr-ninebox-cell pr-ninebox-cell-on' : 'pr-ninebox-cell'}>{cell.label}</div>
                    ))}
                </div>
                <div className="pr-ninebox-x">{`${texts.assessmentScore} →`}</div>
            </div>
            <div className="pr-ninebox-scores">
                <span>{`${texts.assessmentScore}: `}<b>{nineBox.assessmentScoreText}</b></span>
                <span>{`${texts.jobFitScore}: `}<b>{nineBox.jobFitScoreText}</b></span>
            </div>
        </Box>
    );
}

function ReasoningBox({ report }) {
    const { texts, reasoning } = report;
    if (!reasoning.available) return <Box area="reasoning" title={texts.reasoning}><NaBox section={reasoning} texts={texts} /></Box>;
    return (
        <Box area="reasoning" title={texts.reasoning}>
            <table className="pr-reason">
                <thead>
                    <tr>
                        <th className="pr-reason-name">{texts.reasoningColumn}</th>
                        {reasoning.levels.map((level) => <th key={level.key}>{level.label}</th>)}
                        <th className="pr-reason-gauge-col" />
                    </tr>
                </thead>
                <tbody>
                    {reasoning.tests.map((test) => (
                        <tr key={test.type}>
                            <td className="pr-reason-name" style={{ color: test.color }}>{test.label}</td>
                            {reasoning.levels.map((level, index) => {
                                const on = test.available && test.levelIndex === index;
                                return (
                                    <td key={level.key}>
                                        <span className="pr-reason-cell" style={on ? { background: test.color, borderColor: test.color } : undefined} />
                                    </td>
                                );
                            })}
                            <td className="pr-reason-gauge-col">
                                {test.available ? <Gauge test={test} /> : <span className="pr-reason-missing">{test.reasonText}</span>}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </Box>
    );
}

function SummaryPage({ report, page }) {
    const { texts, disc, integrity, candidate } = report;
    const discList = (key) => (disc.available ? <List items={disc[key]} texts={texts} /> : <NaBox section={disc} texts={texts} />);
    return (
        <PageShell report={report} page={page}>
            <div className="pr-grid pr-grid-summary">
                <PersonalBox report={report} simple={false} />
                <Box area="trajectory" title={texts.trajectory}>
                    {candidate.trajectory.available ? (
                        <>
                            <List items={candidate.trajectory.items} texts={texts} />
                            {candidate.trajectory.note ? <div className="pr-note">{candidate.trajectory.note}</div> : null}
                        </>
                    ) : (
                        <NaBox section={candidate.trajectory} texts={texts} />
                    )}
                </Box>
                <NineBoxBox report={report} />
                <CompetencyBarsBox report={report} page={page} area="competencies" wide={false} />
                <ReasoningBox report={report} />
                <Box area="integrity" title={texts.integrity}>
                    {integrity.available ? <Bars rows={integrity.bars} /> : <NaBox section={integrity} texts={texts} />}
                </Box>
                <Box area="strengths" title={texts.strengths}>{discList('strengths')}</Box>
                <Box area="development" title={texts.developmentAreas}>{discList('developmentAreas')}</Box>
            </div>
        </PageShell>
    );
}

function SummarySimplePage({ report, page }) {
    const { texts, summary, competencies, generalView, scales } = report;
    return (
        <PageShell report={report} page={page}>
            <div className="pr-grid pr-grid-summary-simple">
                <PersonalBox report={report} simple />
                <Box area="score" title={texts.overallScore}>
                    <div className="pr-score-big" style={{ color: summary.overallColor }}>{summary.overallScoreText}</div>
                    <div className="pr-score-level" style={{ color: summary.overallColor }}>{summary.overallLevelLabel}</div>
                    <div className="pr-scale pr-scale-list"><ScaleItems scale={scales.competency} /></div>
                </Box>
                <Box area="ranking" title={texts.ranking}><Ranking ranked={competencies.ranked} texts={texts} /></Box>
                <CompetencyBarsBox report={report} page={page} area="bars" wide />
                <Box area="vision" title={texts.generalView}>
                    {generalView.available
                        ? <p className="pr-text pr-clamp-28">{generalView.text}</p>
                        : <NaBox section={generalView} texts={texts} />}
                </Box>
            </div>
        </PageShell>
    );
}

function DiscPage({ report, page }) {
    const { texts, disc, emotional } = report;
    let characteristics;
    let narrative;
    let diamond;
    if (disc.available) {
        const d = disc.charts.diamond;
        characteristics = (
            <>
                <List items={disc.characteristics} texts={texts} />
                <div className="pr-pies">
                    <Pie chart={disc.charts.pies.natural} title={texts.naturalProfile} />
                    <Pie chart={disc.charts.pies.adapted} title={texts.adaptedProfile} />
                    <table className="pr-pie-legend">
                        <thead>
                            <tr><th /><th>{texts.natural}</th><th>{texts.adapted}</th></tr>
                        </thead>
                        <tbody>
                            {disc.legend.map((row) => (
                                <tr key={row.letter}>
                                    <td className="pr-pie-letter"><span className="pr-chip" style={{ background: row.color }} />{row.letter}</td>
                                    <td>{row.naturalText}</td>
                                    <td>{row.adaptedText}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="pr-disc-style"><b>{`${texts.discStyle}:`}</b>{' '}{disc.styleText}</div>
                <div className="pr-disc-legend">{texts.discLegend}</div>
            </>
        );
        narrative = (
            <>
                <Para label={texts.motivators} text={disc.motivatorsText} clamp={4} texts={texts} />
                <Para label={texts.avoids} text={disc.avoidsText} clamp={5} texts={texts} />
                <Para label={texts.communication} text={disc.communicationStyle} clamp={4} texts={texts} />
                <Para label={texts.decisionMaking} text={disc.decisionMaking} clamp={6} texts={texts} />
            </>
        );
        diamond = (
            <div className="pr-diamond-wrap">
                <Diamond diamond={d} />
                <div className="pr-diamond-legend">
                    <div className="pr-diamond-legend-row">
                        <span className="pr-dot-natural" />
                        <span>{`${texts.naturalProfile}: `}<b>{d.natural.code}</b></span>
                    </div>
                    <div className="pr-diamond-legend-row">
                        <span className="pr-dot-adapted" />
                        <span>{`${texts.adaptedProfile}: `}<b>{d.adapted.code}</b></span>
                    </div>
                    <div className="pr-muted">{texts.diamondLegend}</div>
                </div>
            </div>
        );
    } else {
        characteristics = <NaBox section={disc} texts={texts} />;
        narrative = <NaBox section={disc} texts={texts} />;
        diamond = <NaBox section={disc} texts={texts} />;
    }
    return (
        <PageShell report={report} page={page}>
            <div className="pr-page-title">{texts.behavior}</div>
            <div className="pr-grid pr-grid-disc">
                <SubBox area="char" title={texts.characteristics}>{characteristics}</SubBox>
                <PlainBox area="motivators">{narrative}</PlainBox>
                <SubBox area="diamond" title={texts.flexibilityZone}>{diamond}</SubBox>
                <Box area="emotional" title={texts.emotional}>
                    {emotional.available ? <Bars rows={emotional.bars} /> : <NaBox section={emotional} texts={texts} />}
                </Box>
            </div>
        </PageShell>
    );
}

function Cards({ report, page }) {
    const byId = new Map(report.competencies.items.map((item) => [item.id, item]));
    return page.cards.map((id, index) => (
        <CompetencyCard key={id} item={byId.get(id)} area={`c${index + 1}`} texts={report.texts} />
    ));
}

function CompetenciesPage({ report, page }) {
    const { texts, competencies, scales } = report;
    return (
        <PageShell report={report} page={page}>
            <div className="pr-page-title">{texts.competencies}</div>
            <div className={page.showRanking ? 'pr-grid pr-grid-comp-first' : 'pr-grid pr-grid-comp-first pr-no-ranking'}>
                <SubBox area="table" title={texts.competencies}>
                    {competencies.items.length > 0 ? (
                        <>
                            <table className={page.denseTable ? 'pr-comp-table pr-dense' : 'pr-comp-table'}>
                                <thead>
                                    <tr><th>{texts.competencies}</th><th className="pr-num">{texts.score}</th></tr>
                                </thead>
                                <tbody>
                                    {competencies.items.map((item) => (
                                        <tr key={item.id} style={{ background: item.tint }}>
                                            <td>{item.name}</td>
                                            <td className="pr-num" style={{ color: item.color }}>{item.scoreText}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr><td>{texts.overallScore}</td><td className="pr-num">{competencies.averageText}</td></tr>
                                </tfoot>
                            </table>
                            <div className="pr-scale"><ScaleItems scale={scales.competency} /></div>
                        </>
                    ) : (
                        <NaBox section={{ reasonText: texts.reasons.noData }} texts={texts} />
                    )}
                </SubBox>
                {page.showRanking ? (
                    <SubBox area="ranking" title={texts.ranking}><Ranking ranked={competencies.ranked} texts={texts} /></SubBox>
                ) : null}
                <Cards report={report} page={page} />
            </div>
        </PageShell>
    );
}

function CompetencyCardsPage({ report, page }) {
    const { texts } = report;
    return (
        <PageShell report={report} page={page}>
            <div className="pr-page-title">{`${texts.competencies} · ${texts.competencyDetail}`}</div>
            <div className="pr-grid pr-grid-cards"><Cards report={report} page={page} /></div>
        </PageShell>
    );
}

function ConclusionPage({ report, page }) {
    const { texts } = report;
    return (
        <PageShell report={report} page={page}>
            <div className="pr-page-title">{texts.conclusion}</div>
            {page.columns ? (
                <div className="pr-columns">
                    {page.columns.map((column, index) => (
                        <div className="pr-column" key={index}>
                            {column.map((block) => (
                                <div className="pr-section" key={block.key}>
                                    {block.title ? (
                                        <div className="pr-sub-title">{block.continued ? `${block.title} ${texts.continued}` : block.title}</div>
                                    ) : null}
                                    <p className="pr-text">{block.text}</p>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            ) : (
                <NaBox section={report.conclusion} texts={texts} />
            )}
        </PageShell>
    );
}

// Interruptor showNineBoxAnalysis (backend)
function NineBoxAnalysisPage({ report, page }) {
    const { texts, nineBoxAnalysis: analysis, nineBox } = report;
    const title = nineBox && nineBox.available ? `${texts.nineBoxAnalysis} · ${nineBox.label}` : texts.nineBoxAnalysis;
    return (
        <PageShell report={report} page={page}>
            <div className="pr-page-title">{title}</div>
            {analysis.format === 'text' ? (
                <div className="pr-box"><div className="pr-box-body"><p className="pr-text pr-clamp-28">{analysis.text}</p></div></div>
            ) : (
                <div className="pr-grid pr-grid-quad">
                    {analysis.sections.map((section) => (
                        <div className="pr-box" key={section.key}>
                            <div className="pr-sub-title">{section.title}</div>
                            <div className="pr-box-body"><p className="pr-text pr-clamp-14">{section.text}</p></div>
                        </div>
                    ))}
                </div>
            )}
        </PageShell>
    );
}

// Interruptor showDevelopmentPlan (backend)
function DevelopmentPlanPage({ report, page }) {
    const { texts, developmentPlan: plan } = report;
    const group = (title, items) => (
        <div className="pr-plan-group">
            <div className="pr-plan-group-title">{title}</div>
            <List items={items} texts={texts} extraClass="pr-list-sm" />
        </div>
    );
    return (
        <PageShell report={report} page={page}>
            <div className="pr-page-title">{texts.developmentPlan}</div>
            {plan.introduction ? <p className="pr-text pr-plan-intro pr-clamp-3">{plan.introduction}</p> : null}
            <div className="pr-grid pr-grid-plan">
                {plan.periods.map((period) => (
                    <div className="pr-box" key={period.key}>
                        <div className="pr-sub-title">{period.title}</div>
                        <div className="pr-box-body">
                            {group(texts.planObjectives, period.objectives)}
                            {group(texts.planActions, period.actions)}
                            {group(texts.planIndicators, period.indicators)}
                        </div>
                    </div>
                ))}
            </div>
        </PageShell>
    );
}

const PAGES = {
    cover: CoverPage,
    summary: SummaryPage,
    summarySimple: SummarySimplePage,
    disc: DiscPage,
    competencies: CompetenciesPage,
    competencyCards: CompetencyCardsPage,
    nineBoxAnalysis: NineBoxAnalysisPage,
    conclusion: ConclusionPage,
    developmentPlan: DevelopmentPlanPage,
};

/**
 * Una hoja del informe (1056 x 816 px), según `page.type`.
 */
export default function PotentialReportPage({ report, page }) {
    const Page = PAGES[page.type];
    return Page ? <Page report={report} page={page} /> : null;
}
